import { isLiteTier, canHaveDrivers, type DriverCapableRooftop } from '@utils/tiers';

/**
 * The links whose rooftop the viewer can read. Hasura returns `customer: null` for a
 * rooftop outside the viewer's select permission: a program-admin, for one, reads only
 * rooftops enrolled in its program, so a user's other rooftops arrive without a customer.
 */
export function readableRooftopLinks<T extends { customer?: unknown }>(
  links: readonly T[] | null | undefined,
): Array<T & { customer: NonNullable<T['customer']> }> {
  return (links ?? []).filter((link): link is T & { customer: NonNullable<T['customer']> } => link.customer != null);
}

/**
 * Resolve the list of customer IDs the current user should query against,
 * from the user's usertocustomers link records.
 */
export function resolveCustomerIds(
  userToCustomers: { customer_id: bigint | number }[] | undefined,
): number[] {
  return (userToCustomers ?? []).map(utc => Number(utc.customer_id));
}

/**
 * Determine whether the user's linked customers include at least one Lite-tier rooftop.
 * Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
 */
export function resolveHasLiteTier(
  userToCustomers: { customer: { tier: string } }[] | undefined,
): boolean {
  return (userToCustomers ?? []).some(utc => isLiteTier(utc.customer.tier));
}

/**
 * Determine whether the user's linked customers include at least one rooftop that may
 * manage its own drivers — `lite` OR Mobile Service enabled (see canHaveDrivers).
 */
export function resolveHasDriverCapableRooftop(
  userToCustomers: { customer: DriverCapableRooftop }[] | undefined,
): boolean {
  return (userToCustomers ?? []).some(utc => canHaveDrivers(utc.customer));
}
